/**
 * Every site at once (`reporting.consolidated`) and the report builder
 * (`reporting.builder`). Enterprise; both answer 402 below it.
 *
 * Money is a string in its row's own currency. A group with sites in two
 * currencies gets a total for each, never one across both — the screen shows
 * them side by side and adds nothing up itself.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { ApiError } from './client';
import type { DayLine } from './reports';

import { useApi } from './provider';

// --------------------------------------------------------------------------- //
// Every site at once
// --------------------------------------------------------------------------- //

export interface ConsolidatedSite {
  location_id: string;
  name: string;
  code: string;
  currency: string;
  /** How this site writes money and dates. */
  locale: string;
  is_active: boolean;
  net_sales: string;
  orders: number;
  covers: number;
  average_spend: string;
  /** Null when there is nothing before to compare against. */
  net_sales_change: string | null;
  days: DayLine[];
}

export interface ConsolidatedTotal {
  currency: string;
  sites: number;
  net_sales: string;
  orders: number;
  covers: number;
  average_spend: string;
  net_sales_change: string | null;
  days: DayLine[];
}

export interface Consolidated {
  period: { from_date: string; to_date: string };
  sites: ConsolidatedSite[];
  /** One per currency. */
  totals: ConsolidatedTotal[];
}

export interface GroupRange {
  /** Empty means every site you may see. */
  locationIds: string[];
  fromDate: string;
  toDate: string;
}

function sitesQuery(ids: string[]): string {
  return ids.map((id) => `&location_id=${id}`).join('');
}

export function useConsolidated(range: GroupRange, enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: ['reports', 'consolidated', range.locationIds, range.fromDate, range.toDate],
    queryFn: ({ signal }) =>
      api.get<Consolidated>(
        `/v1/reports/consolidated?from_date=${range.fromDate}&to_date=${range.toDate}` +
          sitesQuery(range.locationIds),
        signal,
      ),
    enabled,
    staleTime: 60_000,
  });
}

export interface DrillBillLine {
  name: string;
  quantity: string;
  net: string;
}

export interface DrillBill {
  id: string;
  number: number;
  opened_at: string;
  closed_at: string | null;
  covers: number;
  opened_by: string;
  service: string;
  takeaway: boolean;
  /** Less the service charge: these add up to the day's takings. */
  net: string;
  service_charge: string;
  total: string;
  lines: DrillBillLine[];
}

/** The paid bills behind one site's day. */
export function useDayBills(locationId: string | null, businessDate: string | null) {
  const api = useApi();
  return useQuery({
    queryKey: ['reports', 'consolidated', 'bills', locationId, businessDate],
    queryFn: ({ signal }) =>
      api.get<DrillBill[]>(
        `/v1/reports/consolidated/bills?location_id=${locationId}&business_date=${businessDate}`,
        signal,
      ),
    enabled: Boolean(locationId && businessDate),
  });
}

// --------------------------------------------------------------------------- //
// The report builder
// --------------------------------------------------------------------------- //

export type DimensionKind = 'date' | 'week' | 'month' | 'weekday' | 'hour' | 'text';
export type MeasureKind = 'money' | 'count' | 'quantity';

export interface BuilderDimension {
  key: string;
  label: string;
  kind: DimensionKind;
  filterable: boolean;
}

export interface BuilderMeasure {
  key: string;
  label: string;
  kind: MeasureKind;
  /** A dimension the report must be grouped by for this figure to add up. */
  needs: string | null;
}

export interface BuilderDataset {
  key: string;
  label: string;
  description: string;
  dimensions: BuilderDimension[];
  measures: BuilderMeasure[];
}

/** What can be built from, for the person signed in. */
export function useBuilderDatasets(enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: ['reports', 'builder', 'datasets'],
    queryFn: ({ signal }) => api.get<BuilderDataset[]>('/v1/reports/builder/datasets', signal),
    enabled,
    staleTime: 10 * 60_000,
  });
}

export interface ReportSpec {
  dataset: string;
  dimensions: string[];
  measures: string[];
  /** Dimension → the `key`s to keep. */
  filters: Record<string, string[]>;
  /** Empty means every site you may see. */
  location_ids: string[];
}

export interface ReportRun extends ReportSpec {
  from_date: string;
  to_date: string;
}

export interface ReportCell {
  /** What a filter matches: a date as ISO, a weekday 0 (Monday) to 6. */
  key: string;
  label: string;
}

export interface ReportColumn {
  key: string;
  label: string;
  kind: DimensionKind | MeasureKind;
}

export interface ReportRow {
  currency: string;
  dimensions: ReportCell[];
  /** Null where an average has nothing to divide into. */
  measures: (string | null)[];
}

export interface ReportResult {
  dataset: string;
  from_date: string;
  to_date: string;
  dimensions: ReportColumn[];
  measures: ReportColumn[];
  rows: ReportRow[];
  /** One per currency, over every row. */
  totals: ReportRow[];
  currencies: string[];
  /** More rows than are shown here. The download has every one. */
  truncated: boolean;
}

/** Run a report. 409 with `field` names a choice the builder refuses. */
export function useReportRun(run: ReportRun | null) {
  const api = useApi();
  return useQuery<ReportResult, ApiError>({
    queryKey: ['reports', 'builder', 'run', run],
    queryFn: () => api.post<ReportResult>('/v1/reports/builder/run', run ?? undefined),
    enabled: run !== null && run.measures.length > 0,
    staleTime: 60_000,
    retry: false,
  });
}

/** The values a dimension took in the period, for a filter to offer. */
export function useFilterValues(dataset: string, dimension: string | null, range: GroupRange) {
  const api = useApi();
  return useQuery({
    queryKey: [
      'reports',
      'builder',
      'values',
      dataset,
      dimension,
      range.locationIds,
      range.fromDate,
      range.toDate,
    ],
    queryFn: ({ signal }) =>
      api.get<ReportCell[]>(
        `/v1/reports/builder/values?dataset=${dataset}&dimension=${dimension}` +
          `&from_date=${range.fromDate}&to_date=${range.toDate}` +
          sitesQuery(range.locationIds),
        signal,
      ),
    enabled: Boolean(dimension),
  });
}

/** The path of a built report's CSV, posted with the run as its body. */
export const REPORT_CSV_PATH = '/v1/reports/builder/run.csv';

export type ReportPeriod =
  'last_7_days' | 'last_30_days' | 'last_90_days' | 'this_month' | 'last_month' | 'custom';

export interface SavedReport extends ReportSpec {
  id: string;
  name: string;
  period: ReportPeriod;
  /** Custom periods only. */
  from_date: string | null;
  to_date: string | null;
  updated_at: string;
}

export interface SavedReportInput extends ReportSpec {
  name: string;
  period: ReportPeriod;
  from_date: string | null;
  to_date: string | null;
}

/** The restaurant's saved reports that you could run. */
export function useSavedReports(enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: ['reports', 'builder', 'saved'],
    queryFn: ({ signal }) => api.get<SavedReport[]>('/v1/reports/builder/saved', signal),
    enabled,
  });
}

function useSavedMutation<TInput, TOut>(
  send: (api: ReturnType<typeof useApi>, input: TInput) => Promise<TOut>,
) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<TOut, ApiError, TInput>({
    mutationFn: (input) => send(api, input),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['reports', 'builder', 'saved'] });
    },
  });
}

/** Keep it for everybody. 409 for a name already used. */
export function useSaveReport() {
  return useSavedMutation<SavedReportInput, SavedReport>((api, input) =>
    api.post<SavedReport>('/v1/reports/builder/saved', input),
  );
}

/** Replace what a saved report asks, sent whole. */
export function useChangeReport() {
  return useSavedMutation<{ id: string; report: SavedReportInput }, SavedReport>((api, input) =>
    api.request<SavedReport>(`/v1/reports/builder/saved/${input.id}`, {
      method: 'PUT',
      body: input.report,
    }),
  );
}

export function useRemoveReport() {
  return useSavedMutation<{ id: string }, void>((api, input) =>
    api.request<void>(`/v1/reports/builder/saved/${input.id}`, { method: 'DELETE' }),
  );
}
